import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Upload, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";

const stepLabels = ["Business", "Website", "Industry", "Tone", "Logo", "Colors", "Goals", "Done"];

const industries = [
  "Restaurant & Food", "Retail & Ecommerce", "Health & Wellness", "Beauty & Spa",
  "Fitness", "Real Estate", "Professional Services", "Home Services",
  "Education", "Nonprofit", "Travel", "Tech & SaaS",
  "Automotive", "Events & Entertainment", "Other",
];

const tones = [
  { id: "professional", label: "Professional", desc: "Polished, expert, trustworthy" },
  { id: "friendly", label: "Friendly & Casual", desc: "Warm, approachable, conversational" },
  { id: "bold", label: "Bold & Confident", desc: "Punchy, decisive, high-energy" },
  { id: "playful", label: "Playful & Fun", desc: "Witty, light, entertaining" },
  { id: "luxurious", label: "Luxurious", desc: "Refined, exclusive, premium" },
  { id: "inspirational", label: "Inspirational", desc: "Uplifting, motivational" },
  { id: "minimal", label: "Minimal & Modern", desc: "Clean, direct, no fluff" },
  { id: "other", label: "Other (custom)", desc: "Define your own tone" },
];

const colorPresets = [
  { id: "ocean", label: "Ocean", primary: "#0EA5E9", secondary: "#0369A1" },
  { id: "forest", label: "Forest", primary: "#10B981", secondary: "#047857" },
  { id: "sunset", label: "Sunset", primary: "#F97316", secondary: "#C2410C" },
  { id: "berry", label: "Berry", primary: "#7C3AED", secondary: "#5B21B6" },
  { id: "coral", label: "Coral", primary: "#F43F5E", secondary: "#BE123C" },
  { id: "slate", label: "Slate", primary: "#475569", secondary: "#1E293B" },
];

const goals = [
  { id: "awareness", emoji: "📣", label: "Build brand awareness" },
  { id: "leads", emoji: "🎯", label: "Generate leads" },
  { id: "sales", emoji: "💰", label: "Drive sales" },
  { id: "loyalty", emoji: "❤️", label: "Customer loyalty" },
  { id: "social", emoji: "📱", label: "Grow social following" },
  { id: "seo", emoji: "🔎", label: "Improve SEO" },
];

const StepDot = ({ done, active, idx }) => (
  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
    done ? 'op-step-done' : active ? 'op-step-active' : 'op-step-future'
  }`}>
    {done ? <CheckCircle2 size={16} /> : idx + 1}
  </div>
);

const Onboarding = () => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ marketing_goals: [], primary_color: "#7C3AED", secondary_color: "#6D28D9" });
  const [saving, setSaving] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/onboarding").then(({ data: d }) => {
      if (d) setData((prev) => ({ ...prev, ...d, marketing_goals: d.marketing_goals || [] }));
    }).catch(() => {});
  }, []);

  const update = (patch) => setData((p) => ({ ...p, ...patch }));

  const save = async (patch) => {
    try { await api.put("/onboarding", { ...data, ...patch }); } catch (e) {}
  };

  const next = async () => {
    await save({});
    if (step < 7) setStep(step + 1);
  };

  const finish = async () => {
    setSaving(true);
    try {
      await api.put("/onboarding", data);
      await api.post("/onboarding/complete");
      await refresh();
      toast.success("Welcome to Optimus Prime!");
      nav("/dashboard");
    } catch (e) {
      toast.error("Could not save");
    } finally { setSaving(false); }
  };

  const toggleGoal = (id) => {
    const g = data.marketing_goals || [];
    update({ marketing_goals: g.includes(id) ? g.filter(x => x !== id) : [...g, id] });
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <Logo size={26} />
        <span className="text-sm text-gray-500">Step {step + 1} of 8</span>
      </div>
      <div className="flex-1 flex flex-col items-center px-6 py-10">
        <div className="flex items-center gap-1.5 mb-12 max-w-3xl flex-wrap justify-center" data-testid="onboarding-stepper">
          {stepLabels.map((l, i) => (
            <React.Fragment key={i}>
              <StepDot done={i < step} active={i === step} idx={i} />
              {i < stepLabels.length - 1 && <div className={`w-8 h-0.5 ${i < step ? 'bg-[#7C3AED]' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="w-full max-w-2xl">
          {step === 0 && (
            <div className="text-center" data-testid="onb-step-business">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">What's your business name?</h1>
              <p className="text-gray-500 mb-8">We'll personalize everything you build with us.</p>
              <input value={data.business_name || ""} onChange={(e) => update({ business_name: e.target.value })} placeholder="Acme Coffee Co." className="op-input text-lg text-center max-w-md mx-auto" data-testid="onb-business-name-input" />
            </div>
          )}
          {step === 1 && (
            <div data-testid="onb-step-website">
              <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">Tell us about your business</h1>
              <p className="text-gray-500 mb-8 text-center">Helps us understand your brand.</p>
              <div className="space-y-4">
                <input value={data.website || ""} onChange={(e) => update({ website: e.target.value })} placeholder="Website URL" className="op-input" data-testid="onb-website-input" />
                <textarea value={data.description || ""} onChange={(e) => update({ description: e.target.value })} placeholder="Describe what you do (1-2 sentences)" rows={3} className="op-input" data-testid="onb-description-input" />
                <input value={data.location || ""} onChange={(e) => update({ location: e.target.value })} placeholder="Location (city, state)" className="op-input" data-testid="onb-location-input" />
                <div className="grid grid-cols-2 gap-3">
                  <input placeholder="Instagram handle" className="op-input" onChange={(e) => update({ socials: { ...(data.socials||{}), instagram: e.target.value } })} />
                  <input placeholder="Facebook page" className="op-input" onChange={(e) => update({ socials: { ...(data.socials||{}), facebook: e.target.value } })} />
                </div>
              </div>
            </div>
          )}
          {step === 2 && (
            <div data-testid="onb-step-industry">
              <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">What industry are you in?</h1>
              <p className="text-gray-500 mb-8 text-center">We'll tailor recommendations to your space.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {industries.map((ind) => (
                  <button key={ind} onClick={() => update({ industry: ind })} className={`op-card p-4 text-left text-sm font-medium ${data.industry === ind ? 'border-[#7C3AED] ring-2 ring-purple-100 text-[#7C3AED]' : 'text-gray-700'}`} data-testid={`onb-industry-${ind.toLowerCase().replace(/\s+/g,'-').replace('&','and')}`}>
                    {ind}
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === 3 && (
            <div data-testid="onb-step-tone">
              <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">Pick your brand tone</h1>
              <p className="text-gray-500 mb-8 text-center">Sets the voice for AI-generated content.</p>
              <div className="space-y-2">
                {tones.map((t) => (
                  <button key={t.id} onClick={() => update({ brand_tone: t.id })} className={`w-full op-card p-4 text-left flex items-start gap-3 ${data.brand_tone === t.id ? 'border-[#7C3AED] ring-2 ring-purple-100' : ''}`} data-testid={`onb-tone-${t.id}`}>
                    <span className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center ${data.brand_tone === t.id ? 'border-[#7C3AED]' : 'border-gray-300'}`}>
                      {data.brand_tone === t.id && <span className="w-2.5 h-2.5 rounded-full bg-[#7C3AED]" />}
                    </span>
                    <div>
                      <p className="font-medium text-gray-900">{t.label}</p>
                      <p className="text-sm text-gray-500">{t.desc}</p>
                    </div>
                  </button>
                ))}
                {data.brand_tone === "other" && (
                  <input value={data.brand_tone_other || ""} onChange={(e) => update({ brand_tone_other: e.target.value })} placeholder="Describe your tone..." className="op-input" />
                )}
              </div>
            </div>
          )}
          {step === 4 && (
            <div data-testid="onb-step-logo">
              <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">Upload your logo</h1>
              <p className="text-gray-500 mb-8 text-center">We'll auto-detect from your website if you skip.</p>
              <label className="block border-2 border-dashed border-gray-300 hover:border-[#7C3AED] rounded-xl p-12 text-center cursor-pointer transition-colors">
                <Upload className="mx-auto text-gray-400 mb-3" size={36} />
                <p className="text-sm font-medium text-gray-700">Click to upload or drag and drop</p>
                <p className="text-xs text-gray-500 mt-1">PNG, JPG, WebP, SVG · max 5MB</p>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    const reader = new FileReader();
                    reader.onload = () => update({ logo_url: reader.result });
                    reader.readAsDataURL(f);
                  }
                }} data-testid="onb-logo-upload" />
              </label>
              {data.logo_url && <img src={data.logo_url} alt="logo" className="mx-auto mt-6 h-20 object-contain" />}
            </div>
          )}
          {step === 5 && (
            <div data-testid="onb-step-colors">
              <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">Choose your brand colors</h1>
              <p className="text-gray-500 mb-8 text-center">Used everywhere we generate content for you.</p>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-8">
                {colorPresets.map((p) => (
                  <button key={p.id} onClick={() => update({ color_preset: p.id, primary_color: p.primary, secondary_color: p.secondary })} className={`op-card p-3 ${data.color_preset === p.id ? 'border-[#7C3AED] ring-2 ring-purple-100' : ''}`} data-testid={`onb-color-${p.id}`}>
                    <div className="flex gap-1 mb-2 h-10">
                      <div className="flex-1 rounded" style={{ backgroundColor: p.primary }} />
                      <div className="flex-1 rounded" style={{ backgroundColor: p.secondary }} />
                    </div>
                    <p className="text-xs font-medium text-gray-700">{p.label}</p>
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Primary</label>
                  <div className="flex gap-2">
                    <input type="color" value={data.primary_color} onChange={(e) => update({ primary_color: e.target.value })} className="w-12 h-10 rounded border border-gray-300" />
                    <input value={data.primary_color} onChange={(e) => update({ primary_color: e.target.value })} className="op-input flex-1" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Secondary</label>
                  <div className="flex gap-2">
                    <input type="color" value={data.secondary_color} onChange={(e) => update({ secondary_color: e.target.value })} className="w-12 h-10 rounded border border-gray-300" />
                    <input value={data.secondary_color} onChange={(e) => update({ secondary_color: e.target.value })} className="op-input flex-1" />
                  </div>
                </div>
              </div>
            </div>
          )}
          {step === 6 && (
            <div data-testid="onb-step-goals">
              <h1 className="text-3xl font-bold text-gray-900 mb-2 text-center">What are your marketing goals?</h1>
              <p className="text-gray-500 mb-8 text-center">Select all that apply.</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {goals.map((g) => {
                  const active = data.marketing_goals?.includes(g.id);
                  return (
                    <button key={g.id} onClick={() => toggleGoal(g.id)} className={`op-card p-5 text-center ${active ? 'border-[#7C3AED] ring-2 ring-purple-100' : ''}`} data-testid={`onb-goal-${g.id}`}>
                      <div className="text-3xl mb-2">{g.emoji}</div>
                      <p className="text-sm font-medium text-gray-900">{g.label}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {step === 7 && (
            <div className="text-center" data-testid="onb-step-done">
              <div className="w-20 h-20 mx-auto rounded-full bg-[#EDE9FE] flex items-center justify-center mb-6">
                <CheckCircle2 className="text-[#7C3AED]" size={40} />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-3">You're ready, {data.business_name || "friend"}!</h1>
              <p className="text-gray-500 mb-8 max-w-md mx-auto">Optimus Prime is now tailored to your brand. Let's start creating.</p>
              <button onClick={finish} disabled={saving} className="op-btn-primary mx-auto text-base py-3 px-8" data-testid="onb-complete-btn">
                <span>✨</span> {saving ? "Setting up..." : "Complete Setup"}
              </button>
            </div>
          )}
        </div>

        {step < 7 && (
          <div className="mt-12 flex items-center gap-3">
            {step > 0 && <button onClick={() => setStep(step - 1)} className="op-btn-secondary" data-testid="onb-back-btn">Back</button>}
            <button onClick={next} className="op-btn-primary" data-testid="onb-next-btn">Continue →</button>
          </div>
        )}
      </div>
    </div>
  );
};
export default Onboarding;
